import { CommonModule, NgOptimizedImage } from '@angular/common';
import { AfterViewInit, Component, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMap, GoogleMapsModule, MapCircle, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { PlaceTypeEnum } from '@buspro/shared/common';
import { AddStandardPlaceComponent } from '@buspro/web/admin/shared';
import { StandardPlaceDto, StandardPlacesApi } from '@buspro/web/apis';
import { UpdateStandardPlaceDto } from '@buspro/web/apis';
import { ApiError, CacheKeysEnum, WindowService } from '@buspro/web/core';
import {
  CountryMapComponent,
  PreviewLocationComponent,
  ResourcesLoadingSkeletonsComponent,
  ResourcesQueryErrorComponent,
  StandardPlaceStatusComponent,
} from '@buspro/web/shared';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDropDownDirective, NzDropdownButtonDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective } from 'ng-zorro-antd/page-header';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzOptionComponent, NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import {
  NzTableCellDirective,
  NzTbodyComponent,
  NzThMeasureDirective,
  NzTheadComponent,
  NzTrDirective,
} from 'ng-zorro-antd/table';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { EditStandardPlaceComponent } from '../edit-place/edit-standard-place.component';

interface PlaceMarker {
  id: string;
  position: google.maps.LatLngLiteral;
  title: string;
  label?: google.maps.MarkerLabel;
  options: google.maps.MarkerOptions;
  place: StandardPlaceDto;
}

interface PlaceCircle {
  id: string;
  center: google.maps.LatLngLiteral;
  radius: number;
  options: google.maps.CircleOptions;
  place: StandardPlaceDto;
}

@Component({
  selector: 'admin-standard-places',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    NzDropDownDirective,
    NzDropdownButtonDirective,
    NzDropdownMenuComponent,
    NzMenuDirective,
    NzMenuItemComponent,
    NzPageHeaderComponent,
    NzPageHeaderExtraDirective,
    NzSpinComponent,
    NzTableCellDirective,
    NzTagComponent,
    NzTbodyComponent,
    NzThMeasureDirective,
    NzTheadComponent,
    NzTrDirective,
    PreviewLocationComponent,
    ResourcesLoadingSkeletonsComponent,
    ResourcesQueryErrorComponent,
    CountryMapComponent,
    StandardPlaceStatusComponent,
    EditStandardPlaceComponent,
    ReactiveFormsModule,
    FormsModule,
    NzPaginationModule,
    NzInputModule,
    AddStandardPlaceComponent,
    NzSelectModule,
    NzTooltipDirective,
    NzSegmentedModule,
    GoogleMapsModule,
    NzButtonModule,
    NzCheckboxModule,
  ],
  templateUrl: './standard-places.component.html',
  styleUrl: './standard-places.component.scss',
})
export class StandardPlacesComponent implements AfterViewInit {
  private notification = inject(NzNotificationService);
  private modal = inject(NzModalService);
  private standardPlacesApi = inject(StandardPlacesApi);
  private logger = inject(NGXLogger);
  private windowService = inject(WindowService);

  placeTypeEnum = PlaceTypeEnum;

  // View mode
  viewMode = signal<'table' | 'map'>('table');

  // Map related
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;

  mapMarkers = signal<PlaceMarker[]>([]);
  mapCircles = signal<PlaceCircle[]>([]);
  selectedPlaceForInfo = signal<StandardPlaceDto | null>(null);
  showCircles = signal<boolean>(true);
  mapInitialized = signal<boolean>(false);
  // Default center - will be updated when data loads or country changes
  mapCenter = signal<google.maps.LatLngLiteral>({ lat: 0.3476, lng: 32.5825 }); // Default to Uganda
  mapZoom = signal<number>(6);
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 3,
    fullscreenControl: true,
  };

  /**
   * Get default map center based on selected country
   */
  private getDefaultCenter(): google.maps.LatLngLiteral {
    const country = this.countryControl.value;
    if (country === 'Ghana') {
      return { lat: 7.9465, lng: -1.0232 }; // Ghana center
    }
    // Default to Uganda
    return { lat: 0.3476, lng: 32.5825 };
  }

  showAddModal = false;

  // View mode options for nz-segmented
  viewModeOptions = [
    { label: 'Table', value: 'table' },
    { label: 'Map', value: 'map' },
  ];

  standardPlaceStatsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.STANDARD_PLACE_STATS],
    queryFn: () => lastValueFrom(this.standardPlacesApi.standardPlaceControllerGetStandardPlacesStats()),
  }));

  searchControl = new FormControl('');
  countryControl = new FormControl('Uganda'); // Will be updated in constructor
  typeControl = new FormControl<PlaceTypeEnum | null>(null);
  statusControl = new FormControl<'Pending' | 'Approved' | 'Rejected' | null>(null);
  minRadiusControl = new FormControl<number | null>(null);
  maxRadiusControl = new FormControl<number | null>(null);
  sortByControl = new FormControl<'name' | 'radius'>('name');
  sortOrderControl = new FormControl<'asc' | 'desc'>('asc');

  private readonly COUNTRY_STORAGE_KEY = 'admin-standard-places-last-country';

  currentPage = 1;
  pageSize = 10;

  // Computed page size - use large number for map view to get all results
  computedPageSize = computed(() => {
    return this.viewMode() === 'map' ? 10000 : this.pageSize;
  });

  computedCurrentPage = computed(() => {
    return this.viewMode() === 'map' ? 1 : this.currentPage;
  });

  $places = injectQuery(() => ({
    queryKey: [
      CacheKeysEnum.STANDARD_PLACES,
      this.computedCurrentPage(),
      this.computedPageSize(),
      this.searchControl.value,
      this.countryControl.value,
      this.typeControl.value,
      this.statusControl.value,
      this.minRadiusControl.value,
      this.maxRadiusControl.value,
      this.sortByControl.value,
      this.sortOrderControl.value,
    ],
    queryFn: () =>
      lastValueFrom(
        this.standardPlacesApi.standardPlaceControllerGetStandardPlaces({
          page: this.computedCurrentPage(),
          limit: this.computedPageSize(),
          search: this.searchControl.value || undefined,
          country: this.countryControl.value || undefined,
          type: (this.typeControl.value as any) || undefined,
          status: (this.statusControl.value as any) || undefined,
          minRadius: this.getNormalizedMinRadius(),
          maxRadius: this.getNormalizedMaxRadius(),
          sortBy: this.sortByControl.value || undefined,
          sortOrder: this.sortOrderControl.value || undefined,
        }),
      ),
  }));

  get standardPlaceStats() {
    return (
      this.standardPlaceStatsQuery.data() ?? {
        totalPlaces: 0,
        approvedPlaces: 0,
        terminalCount: 0,
        busStopCount: 0,
        bothTerminalAndBusStopCount: 0,
        countriesCount: 0,
      }
    );
  }

  terminalTypesEnum = PlaceTypeEnum;

  selectedPlace$$ = new BehaviorSubject<StandardPlaceDto | null>(null);
  showEditPlaceModal = false;

  showPreviewLocationModal = false;
  searchTerm = null;
  approvingPlaceId: string | null = null;
  updatingPlaceTypeId: string | null = null;

  deleteMutation = injectMutation(() => ({
    mutationFn: (placeId: string) =>
      lastValueFrom(this.standardPlacesApi.standardPlaceControllerDelete({ id: placeId })),
    onSuccess: () => {
      this.$places.refetch();
      this.notification.success(`Place has been removed`, '');
    },
    onError: (error: ApiError) => {
      if (error.error.status === 400) {
        this.notification.error(
          'Error',
          error.error.message || 'This place cannot be deleted as it is linked to one or more routes.',
        );
      } else {
        this.notification.error('Error', 'An error occurred while deleting the place');
      }
    },
  }));

  confirmDeletion(data: StandardPlaceDto) {
    this.modal.confirm({
      nzTitle: 'Delete Place?',
      nzContent: 'This action will permanently delete the specified place. Are you sure you want to continue',
      nzAutofocus: 'cancel',
      nzOkDanger: true,
      nzOkText: 'Yes, Delete',
      nzMask: true,
      nzMaskClosable: true,
      nzOnOk: () => this.deleteMutation.mutate(data.id),
    });
  }

  approveMutation = injectMutation(() => ({
    mutationFn: (placeId: string) =>
      lastValueFrom(
        this.standardPlacesApi.standardPlaceControllerApproveStandardPlace({
          id: placeId,
        }),
      ),
    onSuccess: () => {
      this.$places.refetch();
      this.standardPlaceStatsQuery.refetch();
      this.approvingPlaceId = null;

      this.notification.success('Place approved', 'The place has been successfully approved');
    },
    onError: (error: ApiError) => {
      this.approvingPlaceId = null;
      this.notification.error('Error', error.error?.message || 'An error occurred while approving the place');
    },
  }));

  approvePlace(place: StandardPlaceDto) {
    this.modal.confirm({
      nzTitle: 'Approve Place?',
      nzContent: `Are you sure you want to approve "${place.name}"? This will make the place available for use by tenants.`,
      nzOkText: 'Yes, Approve',
      nzOkType: 'primary',
      nzMask: true,
      nzMaskClosable: true,
      nzOnOk: () => {
        this.approvingPlaceId = place.id;
        this.approveMutation.mutate(place.id);
      },
    });
  }

  updatePlaceTypeMutation = injectMutation(() => ({
    mutationFn: (payload: { place: StandardPlaceDto; newType: PlaceTypeEnum }) => {
      const updatePayload: UpdateStandardPlaceDto = {
        id: payload.place.id,
        name: payload.place.name,
        googleMapPlaceId: payload.place.googleMapPlaceId,
        lat: payload.place.lat,
        lng: payload.place.lng,
        type: payload.newType,
        radius: payload.place.radius,
        country: payload.place.country,
      };

      return lastValueFrom(
        this.standardPlacesApi.standardPlaceControllerUpdateTerminal({
          id: payload.place.id,
          body: updatePayload,
        }),
      );
    },
    onSuccess: () => {
      this.updatingPlaceTypeId = null;
      this.$places.refetch();
      this.standardPlaceStatsQuery.refetch();
      this.notification.success(
        '✅ Place type updated',
        'The place type has been successfully updated. All related tenant places have been automatically updated.',
      );
    },
    onError: (error: ApiError) => {
      this.updatingPlaceTypeId = null;
      this.notification.error(
        'Error updating place type',
        error.error?.message || 'An error occurred while updating the place type. Please try again.',
      );
    },
  }));

  updatePlaceType(place: StandardPlaceDto, newType: PlaceTypeEnum) {
    // Don't update if type hasn't changed
    if (place.type === newType) {
      return;
    }

    this.updatingPlaceTypeId = place.id;
    this.updatePlaceTypeMutation.mutate({ place, newType });
  }

  getPlaceTypeLabel(type: PlaceTypeEnum): string {
    switch (type) {
      case PlaceTypeEnum.Terminal:
        return 'Terminal';
      case PlaceTypeEnum.BusStop:
        return 'Bus Stop';
      case PlaceTypeEnum.TerminalBusStop:
        return 'Terminal & Bus Stop';
      default:
        return type;
    }
  }

  constructor() {
    console.log('🏗️ [MAP] StandardPlacesComponent constructor called');
    this.logger.log('🏗️ [MAP] StandardPlacesComponent constructor called');

    // Restore last selected country from localStorage
    const defaultCountry = this.getDefaultCountry();
    this.countryControl.setValue(defaultCountry, { emitEvent: false });

    // Log initial view mode
    console.log('🗺️ [MAP] Initial view mode:', this.viewMode());
    this.logger.log('🗺️ [MAP] Initial view mode:', this.viewMode());

    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1; // Reset to first page on search
      this.$places.refetch();
    });
    this.countryControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((country) => {
      // Save selected country to localStorage
      if (country) {
        this.saveLastSelectedCountry(country);
      }
      // Update map center if in map view
      if (this.viewMode() === 'map') {
        this.mapCenter.set(this.getDefaultCenter());
      }
      this.currentPage = 1; // Reset to first page on filter
      this.$places.refetch();
    });
    this.typeControl.valueChanges.subscribe(() => {
      this.currentPage = 1;
      this.$places.refetch();
    });
    this.statusControl.valueChanges.subscribe(() => {
      this.currentPage = 1;
      this.$places.refetch();
    });
    this.minRadiusControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.normalizeRadiusBounds();
      this.currentPage = 1;
      this.$places.refetch();
    });
    this.maxRadiusControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.normalizeRadiusBounds();
      this.currentPage = 1;
      this.$places.refetch();
    });
    this.sortByControl.valueChanges.subscribe(() => {
      this.currentPage = 1;
      this.$places.refetch();
    });
    this.sortOrderControl.valueChanges.subscribe(() => {
      this.currentPage = 1;
      this.$places.refetch();
    });

    // Watch for query data changes to update map markers/circles
    effect(
      () => {
        const queryData = this.$places.data();
        const mode = this.viewMode();

        console.log('🔄 [MAP] Effect triggered', {
          hasData: !!queryData,
          itemsCount: queryData?.items?.length || 0,
          mode,
          mapInitialized: this.mapInitialized(),
        });

        this.logger.log('🔄 [MAP] Effect triggered', {
          hasData: !!queryData,
          itemsCount: queryData?.items?.length || 0,
          mode,
          mapInitialized: this.mapInitialized(),
        });

        if (queryData && mode === 'map' && queryData.items && queryData.items.length > 0) {
          console.log('✅ [MAP] Effect: updating map with data', {
            itemsCount: queryData.items.length,
            viewMode: mode,
          });

          this.logger.log('✅ [MAP] Effect: updating map with data', {
            itemsCount: queryData.items.length,
            viewMode: mode,
          });

          this.updateMapMarkersAndCircles();
          // Delay to ensure map is rendered
          this.windowService.nativeWindow.setTimeout(() => {
            this.fitMapBounds();
          }, 500);
        } else if (mode === 'map' && queryData && (!queryData.items || queryData.items.length === 0)) {
          console.warn('⚠️ [MAP] Effect: Map mode but no data', {
            hasData: !!queryData,
            itemsCount: queryData?.items?.length || 0,
          });

          this.logger.warn('⚠️ [MAP] Effect: Map mode but no data', {
            hasData: !!queryData,
            itemsCount: queryData?.items?.length || 0,
          });

          // Update default center based on country when no places
          this.mapCenter.set(this.getDefaultCenter());
          this.mapZoom.set(6);
        }
      },
      { allowSignalWrites: true },
    );

    console.log('✅ [MAP] StandardPlacesComponent constructor complete');
    this.logger.log('✅ [MAP] StandardPlacesComponent constructor complete');
  }

  private normalizeRadiusBounds() {
    const min = this.minRadiusControl.value;
    const max = this.maxRadiusControl.value;
    if (min != null && min < 0) this.minRadiusControl.setValue(0, { emitEvent: false });
    if (max != null && max < 0) this.maxRadiusControl.setValue(0, { emitEvent: false });
    const newMin = this.minRadiusControl.value;
    const newMax = this.maxRadiusControl.value;
    if (newMin != null && newMax != null && newMin > newMax) {
      // swap to keep UX smooth
      this.minRadiusControl.setValue(newMax, { emitEvent: false });
      this.maxRadiusControl.setValue(newMin, { emitEvent: false });
    }
  }

  private getNormalizedMinRadius(): number | undefined {
    const v = this.minRadiusControl.value;
    return v != null && !Number.isNaN(v) ? v : undefined;
  }

  private getNormalizedMaxRadius(): number | undefined {
    const v = this.maxRadiusControl.value;
    return v != null && !Number.isNaN(v) ? v : undefined;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.$places.refetch();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when changing page size
    this.$places.refetch();
  }

  refreshPlaces() {
    this.$places.refetch();
    this.standardPlaceStatsQuery.refetch();
  }

  /**
   * Get default country from localStorage or fallback to 'Uganda'
   */
  private getDefaultCountry(): string {
    try {
      const savedCountry = localStorage.getItem(this.COUNTRY_STORAGE_KEY);
      if (savedCountry && (savedCountry === 'Uganda' || savedCountry === 'Ghana')) {
        return savedCountry;
      }
    } catch (_error) {
      // Silently fail if localStorage is not available
    }
    return 'Uganda';
  }

  /**
   * Save selected country to localStorage
   */
  private saveLastSelectedCountry(country: string): void {
    try {
      localStorage.setItem(this.COUNTRY_STORAGE_KEY, country);
    } catch (_error) {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Handle view mode change
   */
  onViewModeChange(mode: 'table' | 'map' | number): void {
    console.log('🗺️ [MAP] onViewModeChange called with:', mode, 'type:', typeof mode);
    this.logger.log('🗺️ [MAP] onViewModeChange called with:', mode, 'type:', typeof mode);

    // nz-segmented sometimes returns index instead of value, so we need to handle both
    let actualMode: 'table' | 'map';
    if (typeof mode === 'number') {
      actualMode = mode === 0 ? 'table' : 'map';
      console.log('🔄 [MAP] Converted index', mode, 'to mode:', actualMode);
      this.logger.log('🔄 [MAP] Converted index', mode, 'to mode:', actualMode);
    } else {
      actualMode = mode;
    }

    console.log('✅ [MAP] Setting view mode to:', actualMode);
    this.logger.log('✅ [MAP] Setting view mode to:', actualMode);

    this.viewMode.set(actualMode);

    if (mode === 'map') {
      this.logger.log('Switching to map view...');
      // Update default center based on country
      const center = this.getDefaultCenter();
      this.logger.log('Setting map center to:', center);
      this.mapCenter.set(center);

      // Refetch data with new page size for map view
      this.logger.log('Refetching data for map view...');
      this.$places.refetch().then(() => {
        const data = this.$places.data();
        this.logger.log('Data refetched for map:', {
          hasData: !!data,
          itemsCount: data?.items?.length || 0,
          mapInitialized: this.mapInitialized(),
        });

        // Update markers/circles after data is fetched
        this.windowService.nativeWindow.setTimeout(() => {
          this.logger.log('Updating map markers and circles...');
          this.updateMapMarkersAndCircles();
          this.logger.log('Fitting map bounds...');
          this.fitMapBounds();
        }, 300);
      });
    } else {
      // Just refetch for table view
      this.$places.refetch();
    }
  }

  /**
   * Update map markers and circles from current filtered places
   */
  private updateMapMarkersAndCircles(): void {
    const places = this.$places.data()?.items || [];

    this.logger.log('🗺️ [MAP] Updating map markers and circles', {
      placesCount: places.length,
      viewMode: this.viewMode(),
    });

    if (places.length === 0) {
      this.logger.warn('⚠️ [MAP] No places to display on map');
      this.mapMarkers.set([]);
      this.mapCircles.set([]);
      return;
    }

    // Build markers
    const markers: PlaceMarker[] = places.map((place) => ({
      id: place.id,
      position: { lat: place.lat, lng: place.lng },
      title: place.name,
      options: {
        icon: this.getMarkerIcon(place.type, place.status),
        animation: google.maps.Animation.DROP,
        optimized: true,
      },
      place,
    }));

    // Build circles
    const circles: PlaceCircle[] = places.map((place) => ({
      id: place.id,
      center: { lat: place.lat, lng: place.lng },
      radius: place.radius,
      options: {
        fillColor: this.getCircleColor(place.type),
        fillOpacity: 0.2,
        strokeColor: this.getCircleColor(place.type),
        strokeOpacity: 0.8,
        strokeWeight: 2,
        clickable: true,
        zIndex: 1,
      },
      place,
    }));

    this.mapMarkers.set(markers);
    this.mapCircles.set(circles);

    this.logger.log('✅ [MAP] Map markers and circles updated', {
      markersCount: markers.length,
      circlesCount: circles.length,
      firstMarker: markers[0] ? { name: markers[0].title, position: markers[0].position } : null,
    });
  }

  /**
   * Get marker icon based on place type and status
   */
  private getMarkerIcon(type: StandardPlaceDto['type'], _status: StandardPlaceDto['status']): google.maps.Icon {
    const baseUrl = 'http://maps.google.com/mapfiles/ms/icons/';
    let iconColor = 'blue';

    // Color by type
    if (type === PlaceTypeEnum.Terminal) {
      iconColor = 'blue';
    } else if (type === PlaceTypeEnum.BusStop) {
      iconColor = 'orange';
    } else if (type === PlaceTypeEnum.TerminalBusStop) {
      iconColor = 'green';
    }

    // Note: Opacity could be adjusted based on status in the future
    // const opacity = status === 'Pending' ? '0.7' : '1.0';

    return {
      url: `${baseUrl}${iconColor}-dot.png`,
      scaledSize: new google.maps.Size(32, 32),
      anchor: new google.maps.Point(16, 32),
    };
  }

  /**
   * Get circle color based on place type
   */
  private getCircleColor(type: StandardPlaceDto['type']): string {
    switch (type) {
      case PlaceTypeEnum.Terminal:
        return '#1890ff'; // Blue
      case PlaceTypeEnum.BusStop:
        return '#fa8c16'; // Orange
      case PlaceTypeEnum.TerminalBusStop:
        return '#52c41a'; // Green
      default:
        return '#722ed1'; // Purple (fallback)
    }
  }

  /**
   * Handle map initialization
   */
  onMapInitialized(map: google.maps.Map): void {
    this.mapInitialized.set(true);
    this.logger.log('🗺️ [MAP] Google Map initialized', {
      mapType: map.getMapTypeId(),
      center: map.getCenter()?.toJSON(),
      zoom: map.getZoom(),
      markersCount: this.mapMarkers().length,
    });

    // Trigger fit bounds after map is ready if we have markers
    if (this.mapMarkers().length > 0) {
      this.logger.log('🎯 [MAP] Map has markers, fitting bounds...');
      this.windowService.nativeWindow.setTimeout(() => this.fitMapBounds(), 100);
    } else {
      this.logger.warn('⚠️ [MAP] Map initialized but no markers to display');
    }
  }

  /**
   * Fit map bounds to show all markers
   */
  fitMapBounds(): void {
    const markers = this.mapMarkers();

    this.logger.log('🎯 [MAP] fitMapBounds called', {
      markersCount: markers.length,
      hasMap: !!this.map,
      hasGoogleMap: !!this.map?.googleMap,
      mapInitialized: this.mapInitialized(),
    });

    if (!markers.length) {
      this.logger.warn('⚠️ [MAP] No markers to fit bounds');
      return;
    }

    if (!this.map?.googleMap) {
      this.logger.warn('⚠️ [MAP] Map not initialized yet, retrying...');
      this.windowService.nativeWindow.setTimeout(() => this.fitMapBounds(), 200);
      return;
    }

    try {
      const bounds = new google.maps.LatLngBounds();

      // Extend bounds with all marker positions
      markers.forEach((marker) => {
        bounds.extend(marker.position);
      });

      // Also extend with circle boundaries if circles are visible
      if (this.showCircles()) {
        const circles = this.mapCircles();
        circles.forEach((circle) => {
          bounds.extend(circle.center);
          // Extend bounds to include circle radius
          if (google.maps.geometry?.spherical) {
            const north = google.maps.geometry.spherical.computeOffset(
              new google.maps.LatLng(circle.center.lat, circle.center.lng),
              circle.radius,
              0,
            );
            const south = google.maps.geometry.spherical.computeOffset(
              new google.maps.LatLng(circle.center.lat, circle.center.lng),
              circle.radius,
              180,
            );
            const east = google.maps.geometry.spherical.computeOffset(
              new google.maps.LatLng(circle.center.lat, circle.center.lng),
              circle.radius,
              90,
            );
            const west = google.maps.geometry.spherical.computeOffset(
              new google.maps.LatLng(circle.center.lat, circle.center.lng),
              circle.radius,
              270,
            );
            bounds.extend(north);
            bounds.extend(south);
            bounds.extend(east);
            bounds.extend(west);
          }
        });
      }

      // Fit bounds with padding
      this.map.googleMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      this.logger.log('✅ [MAP] Map bounds fitted successfully', { markersCount: markers.length });
    } catch (error) {
      this.logger.error('❌ [MAP] Error fitting map bounds:', error);
    }
  }

  /**
   * Handle marker click
   */
  onMarkerClick(marker: PlaceMarker): void {
    this.selectedPlaceForInfo.set(marker.place);
  }

  /**
   * Open preview modal for selected place
   */
  openPreviewModal(place: StandardPlaceDto): void {
    this.selectedPlace$$.next(place);
    this.showPreviewLocationModal = true;
  }

  /**
   * Open edit modal for selected place
   */
  openEditModal(place: StandardPlaceDto): void {
    this.selectedPlace$$.next(place);
    this.showEditPlaceModal = true;
  }

  /**
   * Toggle circles visibility
   */
  toggleCircles(): void {
    this.showCircles.set(!this.showCircles());
  }

  /**
   * Get filtered places for map view (all results, no pagination)
   */
  getFilteredPlacesForMap(): StandardPlaceDto[] {
    return this.$places.data()?.items || [];
  }

  /**
   * Get visible places count (for map view)
   */
  getVisiblePlacesCount(): number {
    return this.mapMarkers().length;
  }

  ngAfterViewInit(): void {
    // Initialize map markers if in map view
    if (this.viewMode() === 'map') {
      const data = this.$places.data();
      if (data?.items && data.items.length > 0) {
        this.logger.log('Initializing map view in ngAfterViewInit', {
          itemsCount: data.items.length,
        });
        this.windowService.nativeWindow.setTimeout(() => {
          this.updateMapMarkersAndCircles();
          this.fitMapBounds();
        }, 500);
      } else {
        // Set default center based on country
        this.mapCenter.set(this.getDefaultCenter());
        this.mapZoom.set(6);
      }
    }
  }
}
